import { get, post, patch, del } from './apiClient';

export interface Job {
  id: string;
  job_title: string;
  department: string;
  employment_type: string;
  application_deadline: string;
  minimum_reference_level: string;
  required_number_of_references: number;
  description?: string;
  status?: string; // Add status field to identify completed jobs
}

export interface Question {
  label: string;
  group: string;
  is_active: boolean;
  sort_order: number;
  weight: number;
}

export interface JobWithQuestions {
  job: {
    job_title: string;
    department: string;
    employment_type: string;
    application_deadline: string;
    minimum_reference_level: string;
    required_number_of_references: number;
    description?: string;
    status?: string;
  };
  questions: Question[];
}

export interface JobCreateDTO {
  job_title: string;
  department: string;
  employment_type: string;
  application_deadline: string;
  minimum_reference_level: string;
  required_number_of_references: number;
  description?: string;
}

export interface JobUpdateDTO {
  job_title?: string;
  department?: string;
  employment_type?: string;
  application_deadline?: string;
  minimum_reference_level?: string;
  required_number_of_references?: number;
  description?: string;
}

export const jobsApi = {
  // Get all jobs from the user's organization
  getAll: async (): Promise<Job[]> => {
    return get<Job[]>('/jobs/');
  },

  // Get a single job by ID
  getById: async (jobId: string): Promise<Job> => {
    return get<Job>(`/jobs/${jobId}`);
  },

  // Create a new job with questions
  createWithQuestions: async (data: JobWithQuestions): Promise<Job> => {
    const payload = {
      job: {
        job_title: data.job.job_title,
        department: data.job.department,
        employment_type: data.job.employment_type,
        application_deadline: data.job.application_deadline,
        minimum_reference_level: data.job.minimum_reference_level,
        required_number_of_references: data.job.required_number_of_references,
        description: data.job.description
      },
      questions: data.questions.map(question => ({
        label: question.label,
        group: question.group,
        is_active: true,
        sort_order: 0,
        weight: question.weight
      }))
    };

    return post<Job>('/jobs/with-questions', payload);
  },

  // Update a job
  update: async (jobId: string, jobData: JobUpdateDTO): Promise<Job> => {
    return patch<Job>(`/jobs/${jobId}`, jobData);
  },

  // Delete a job
  delete: async (jobId: string): Promise<void> => {
    return del<void>(`/jobs/${jobId}`);
  },

  // Complete a job
  complete: async (jobId: string): Promise<Job> => {
    return patch<Job>(`/jobs/${jobId}/complete`, {});
  },
  
  // Cancel a job
  cancel: async (jobId: string): Promise<Job> => {
    return patch<Job>(`/jobs/${jobId}/cancel`, {});
  },
  
  // Get the final report URL for a completed job
  getFinalReportUrl: (jobId: string): string => {
    return `${import.meta.env.VITE_API_URL || 'http://localhost:10000'}/jobs/${jobId}/final_report`;
  },
  
  // Fetch the final report HTML content for a completed job
  getFinalReport: async (jobId: string): Promise<string> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:10000'}/jobs/${jobId}/final_report`, {
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Accept': 'text/html',
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch final report: ${response.status} ${response.statusText}`);
    }
    
    return response.text(); // Return the HTML content as a string
  },

  // Get candidates for a specific job
  getCandidates: async (jobId: string): Promise<any[]> => {
    try {
      // Make sure to filter by jobId in the query
      const response = await get<any[]>('/candidates', {
        params: { jobId }
      });
      
      // Filter candidates on the client side as well to ensure correct matching
      return (response || []).filter(candidate => candidate.jobId === jobId);
    } catch (error) {
      console.error(`Error fetching candidates for job ${jobId}:`, error);
      return [];
    }
  }
};